"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AudioAnalysisHelper = void 0;
class AudioAnalysisHelper {
    /**
     * Start audio analysis for equalizer visualization
     * This method attempts to capture the currently playing audio and analyze its frequency data
     */
    static async startAudioAnalysis(component) {
        try {
            // Clean up any existing audio analysis
            this.stopAudioAnalysis(component);
            // Initialize Web Audio API
            component.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            // Check if we can get the audio element from the conversation service
            const audioElement = this.getPlayingAudioElement(component);
            if (audioElement) {
                // Create audio source and analyzer
                const source = component.audioContext.createMediaElementSource(audioElement);
                component.analyserNode = component.audioContext.createAnalyser();
                component.analyserNode.fftSize = 256;
                const bufferLength = component.analyserNode.frequencyBinCount;
                component.dataArray = new Uint8Array(bufferLength);
                // Connect audio graph: source -> analyzer -> destination
                source.connect(component.analyserNode);
                source.connect(component.audioContext.destination);
                // Start animation
                this.animateEqualizer(component);
                console.log('🎵 [VoiceChat] Audio analysis started with real audio element');
            }
            else {
                // Fallback to synthetic animation
                this.animateSyntheticEqualizer(component);
                console.log('🎵 [VoiceChat] Using synthetic equalizer animation (no audio element found)');
            }
        }
        catch (error) {
            console.error('💥 [VoiceChat] Failed to start audio analysis:', error);
            // Fallback to synthetic animation
            this.animateSyntheticEqualizer(component);
        }
    }
    /**
     * Stop audio analysis and cleanup resources
     */
    static stopAudioAnalysis(component) {
        // Cancel animation frame
        if (component.equalizerAnimationFrame) {
            cancelAnimationFrame(component.equalizerAnimationFrame);
            component.equalizerAnimationFrame = null;
        }
        // Cleanup audio context
        if (component.audioContext && component.audioContext.state !== 'closed') {
            component.audioContext.close().catch(console.error);
        }
        component.audioContext = null;
        component.analyserNode = null;
        component.dataArray = null;
        // Reset equalizer to default state
        component.equalizerLevels = [10, 20, 30, 40, 50];
    }
    /**
     * Animate equalizer bars based on real audio frequency data
     */
    static animateEqualizer(component) {
        if (!component.analyserNode || !component.dataArray || !component.isSpeaking) {
            return;
        }
        // Get frequency data
        if (component.dataArray) {
            try {
                component.analyserNode.getByteFrequencyData(component.dataArray);
            }
            catch (error) {
                console.warn('Failed to get frequency data:', error);
                return;
            }
        }
        // Map frequency data to 5 equalizer bars
        // Split the frequency spectrum into 5 bands
        const bandSize = Math.floor(component.dataArray.length / 5);
        const newLevels = [];
        for (let i = 0; i < 5; i++) {
            const startIndex = i * bandSize;
            const endIndex = Math.min(startIndex + bandSize, component.dataArray.length);
            // Calculate average amplitude for this band
            let sum = 0;
            for (let j = startIndex; j < endIndex; j++) {
                sum += component.dataArray[j];
            }
            const average = sum / (endIndex - startIndex);
            // Convert to height (0-50 range)
            const height = Math.max(5, Math.min(50, (average / 255) * 50));
            newLevels.push(height);
        }
        // Update equalizer levels
        component.equalizerLevels = newLevels;
        // Continue animation
        component.equalizerAnimationFrame = requestAnimationFrame(() => this.animateEqualizer(component));
    }
    /**
     * Animate equalizer with synthetic data when real audio analysis is not available
     */
    static animateSyntheticEqualizer(component) {
        if (!component.isSpeaking) {
            return;
        }
        // Generate synthetic data that looks like audio visualization
        const newLevels = component.equalizerLevels.map((currentLevel, index) => {
            // Create wave-like motion with different frequencies for each bar
            const time = Date.now() * 0.01;
            const frequency = 0.5 + (index * 0.3);
            const amplitude = 20 + Math.sin(time * frequency) * 15;
            const noise = (Math.random() - 0.5) * 10;
            // Smooth transition
            const targetLevel = Math.max(5, Math.min(50, amplitude + noise));
            const smoothedLevel = currentLevel + (targetLevel - currentLevel) * 0.3;
            return Math.round(smoothedLevel);
        });
        component.equalizerLevels = newLevels;
        // Continue animation
        component.equalizerAnimationFrame = requestAnimationFrame(() => this.animateSyntheticEqualizer(component));
    }
    /**
     * Attempt to get the currently playing audio element
     * This is a best-effort approach to find the audio element used by the conversation service
     */
    static getPlayingAudioElement(component) {
        try {
            // Try to get audio element from conversation service if it exposes it
            if (component.conversationAgentService && typeof component.conversationAgentService.getAudioElement === 'function') {
                return component.conversationAgentService.getAudioElement();
            }
            // Fallback: search for audio elements in the document
            const audioElements = document.querySelectorAll('audio');
            for (let i = 0; i < audioElements.length; i++) {
                const audio = audioElements[i];
                if (!audio.paused && !audio.ended) {
                    return audio;
                }
            }
            return null;
        }
        catch (error) {
            console.error('Failed to get audio element:', error);
            return null;
        }
    }
    /**
     * Setup audio element analysis for equalizer visualization
     */
    static setupAudioElementAnalysis(component, audio) {
        try {
            if (!component.audioContext) {
                component.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }
            // Create audio source and analyzer
            const source = component.audioContext.createMediaElementSource(audio);
            component.analyserNode = component.audioContext.createAnalyser();
            component.analyserNode.fftSize = 32;
            const bufferLength = component.analyserNode.frequencyBinCount;
            component.dataArray = new Uint8Array(bufferLength);
            // Connect audio graph
            source.connect(component.analyserNode);
            source.connect(component.audioContext.destination);
        }
        catch (error) {
            console.warn('Could not setup audio analysis:', error);
            // Continue without audio analysis
        }
    }
}
exports.AudioAnalysisHelper = AudioAnalysisHelper;
